import {
	createContext,
	Dispatch,
	useReducer,
	useEffect,
	useCallback,
	useContext,
} from 'react';

import { UASParams, UASFilter } from 'utils/UASTypes';
import createProgressToast from 'utils/progressToast';

import { FileRef } from 'typing/endpoints';

type State = {
	isExporting: boolean;
	toExport?: string[];
	exportPromise: (params: UASParams) => Promise<FileRef>;
	hidden?: boolean;
	field?: string;
	exportFilters?: UASFilter[];
};

type Actions =
	| { type: 'StartExport' }
	| { type: 'CancelExport' }
	| { type: 'Add'; id: string }
	| { type: 'Remove'; id: string }
	| { type: 'SelectOne'; id: string }
	| { type: 'SelectAll' }
	| { type: 'DeselectAll' }
	| { type: 'SetFilters'; exportFilters?: UASFilter[] };

const reducer = (state: State, action: Actions): State => {
	switch (action.type) {
		case 'StartExport':
			return { ...state, isExporting: true, toExport: [] };
		case 'CancelExport':
			return { ...state, isExporting: false };
		case 'Add':
			return {
				...state,
				toExport: [action.id, ...(state.toExport ?? [])],
			};
		case 'Remove':
			return {
				...state,
				toExport: (state.toExport ?? []).filter(id => id !== action.id),
			};
		case 'SelectOne':
			return { ...state, toExport: [action.id] };
		case 'SelectAll':
			return { ...state, toExport: undefined };
		case 'DeselectAll':
			return { ...state, toExport: [] };

		case 'SetFilters':
			return { ...state, exportFilters: action.exportFilters };
		default:
			throw Error('Unknown action');
	}
};

const ApiExportContext = createContext<
	[State, Dispatch<Actions>] | [null, null]
>([null, null]);

export const useExportContext = () => {
	const [state, dispatch] = useContext(ApiExportContext);

	const toExport = state?.toExport;

	const exportPromise = state?.exportPromise;
	const exportFilters = state?.exportFilters;
	const field = state?.field;
	const exportData = useCallback(async () => {
		if (!exportPromise) {
			return;
		}

		const filter: UASFilter[] | undefined = toExport
			? [
					{
						operation: 'OR',
						filter: (toExport ?? []).map(value => ({
							field: field ?? 'id',
							operation: 'EQ',
							//prevents UAS crash
							value: value ?? 'undefined',
						})),
					},
			  ]
			: exportFilters;

		const { successUpdate, errorUpdate } = createProgressToast(
			'Záznamy se exportují...',
		);
		try {
			const response = await exportPromise({ filter });

			const link = document.createElement('a');
			link.href = `/api/files/${response.id}`;
			link.click();

			successUpdate('Export proběhl úspěšně.');
		} catch (err) {
			errorUpdate('Nepodařilo sa exportovat vybrané objekty. Zkuste to znovu.');
		}
	}, [toExport, exportPromise, field, exportFilters]);

	return { state, dispatch, exportData };
};

export const useExportItem = (id: string) => {
	const { state, dispatch } = useExportContext();
	const exporting = Boolean(state?.isExporting);
	const selected =
		state?.isExporting &&
		(state.toExport === undefined || (state.toExport?.indexOf(id) ?? -1) >= 0);

	const toggleSelected = useCallback(
		() => dispatch?.({ type: selected ? 'Remove' : 'Add', id }),
		[dispatch, selected, id],
	);

	return {
		exporting,
		selected,
		toggleSelected,
	};
};

export const useExportProvider = ({
	exportFilters,
	...args
}: Pick<State, 'exportFilters' | 'exportPromise' | 'hidden' | 'field'>) => {
	const [state, dispatch] = useReducer(reducer, {
		isExporting: false,
		exportFilters,

		...args,
	});
	useEffect(() => dispatch({ type: 'SetFilters', exportFilters }), [
		exportFilters,
	]);
	return { state, dispatch, Provider: ApiExportContext.Provider } as const;
};
